import React, {FC, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {Loader, LoaderVariantsEnum} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {usePrevious} from '../../../../srcCommon/helpers/hookHelpers';
import {
  CardAttributeUpdateAction,
  cardAttributeUpdateReducer,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {updateCardAttributeAsyncAction} from '../../../store/data/cards/cardsActions';
import {ContactDetailsVersionsEnum, useContactDetailsContext} from '../contactDetailsContext';
import {buildContactDetailsEditEvent, buildEditTrackingPayload} from '../contactDetailsTrackingHelpers';
import {AccountContainer} from './accountContainer';
import {ContactsSectionContainer} from './contactsSectionContainer';
import {CustomAttributesSectionContainer} from './customAttributesSectionContainer';
import {Description} from './description';
import {LinksSection} from './linksSection';

/*
 * Types.
 */

interface ContactInformationProps {
  card: CardModel | undefined;
  contact?: VisualContactModel;
}

/*
 * Styles.
 */

const StyledLoadedContainerDiv = styled.div`
  margin-top: -8px;
  margin-bottom: 8px;
`;

/*
 * Component.
 */

export const ContactInformation: FC<ContactInformationProps> = (props) => {
  const {card, contact} = props;
  const {
    panelId,
    version,
    conversationNamespace,
    onTrackEvent,
    createNewCardAsync,
    storedActionsContext: {
      storedActions,
      unsavedCard,
      onSetUnsavedCard,
      clearStoredActions,
      onUpdateUnsavedCard,
    },
  } = useContactDetailsContext();
  const previousCard = usePrevious(card);

  const isCreatingCard = storedActions.length > 0;

  const reduxDispatch = useAppDispatch();
  const updateCardAttributeAsync = useCallback(
    async (action: CardAttributeUpdateAction) => {
      if (!card) {
        return undefined;
      }

      const editTrackingPayload = buildEditTrackingPayload(action, card);
      if (onTrackEvent && editTrackingPayload) {
        onTrackEvent(buildContactDetailsEditEvent(action, card));
      }

      return reduxDispatch(
        updateCardAttributeAsyncAction({
          card,
          action,
          shouldShowUndoBanner: true,
          panelId,
        }),
      );
    },
    [reduxDispatch, onTrackEvent, card, panelId],
  );

  useEffect(() => {
    if (!previousCard || previousCard !== card) {
      onSetUnsavedCard(card);
    }
  }, [card, onSetUnsavedCard, previousCard]);

  useEffect(() => {
    // If we have any stored actions, execute the action on the new card
    if (storedActions.length > 0 && card) {
      const nextCard = storedActions.reduce(
        (nextCardReduced, action) => cardAttributeUpdateReducer(nextCardReduced, action),
        card,
      );
      onSetUnsavedCard(nextCard);
      clearStoredActions();
    }
  }, [card, storedActions, onSetUnsavedCard, clearStoredActions]);

  const updateUnsavedCard = async (action: CardAttributeUpdateAction) => {
    onUpdateUnsavedCard(action);
    // If we do not have a card, create one and store the action we need to perform.
    if (!unsavedCard && contact) {
      await createNewCardAsync(
        contact.name || contact.contact.handle,
        contact.contact,
        conversationNamespace,
      );
    }
  };

  return (
    <>
      <ContactsSectionContainer
        panelId={panelId}
        card={unsavedCard}
        contact={contact}
        updateCardAttributeAsync={updateCardAttributeAsync}
        updateUnsavedCard={updateUnsavedCard}
      />
      {unsavedCard && (
        <>
          {version === ContactDetailsVersionsEnum.VERSION_1 && (
            <AccountContainer
              card={unsavedCard}
              updateCardAttributeAsync={updateCardAttributeAsync}
              updateUnsavedCard={updateUnsavedCard}
            />
          )}
          <Description
            card={unsavedCard}
            updateCardAttributeAsync={updateCardAttributeAsync}
            updateUnsavedCard={updateUnsavedCard}
          />
          <LinksSection
            card={unsavedCard}
            updateCardAttributeAsync={updateCardAttributeAsync}
            updateUnsavedCard={updateUnsavedCard}
          />
          <CustomAttributesSectionContainer
            card={unsavedCard}
            updateCardAttributeAsync={updateCardAttributeAsync}
            updateUnsavedCard={updateUnsavedCard}
          />
        </>
      )}
      {isCreatingCard && (
        <StyledLoadedContainerDiv>
          <Loader size={VisualSizesEnum.SMALL} variant={LoaderVariantsEnum.BASE} isEnabled />
        </StyledLoadedContainerDiv>
      )}
    </>
  );
};
